package com.trevorwiebe.timesheet.punch.data

import com.trevorwiebe.timesheet.core.data.FirestoreListenerRegistry
import com.trevorwiebe.timesheet.core.data.Utils.errorWrapper
import com.trevorwiebe.timesheet.core.domain.CoreRepository
import com.trevorwiebe.timesheet.core.domain.TSResult
import com.trevorwiebe.timesheet.core.domain.dto.DatabaseUserDto
import com.trevorwiebe.timesheet.core.domain.dto.PunchDto
import com.trevorwiebe.timesheet.core.domain.dto.RateDto
import com.trevorwiebe.timesheet.core.domain.dto.TimeSheetDto
import com.trevorwiebe.timesheet.core.domain.model.Punch
import com.trevorwiebe.timesheet.core.domain.model.TimeSheet
import com.trevorwiebe.timesheet.core.domain.model.toPunch
import com.trevorwiebe.timesheet.core.domain.model.toPunchDto
import com.trevorwiebe.timesheet.core.domain.model.toRate
import com.trevorwiebe.timesheet.core.domain.model.toTimeSheet
import com.trevorwiebe.timesheet.core.domain.model.toTimeSheetDto
import com.trevorwiebe.timesheet.punch.domain.PunchRepository
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.map
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import kotlin.time.ExperimentalTime

class PunchRepositoryImpl(
    private val firebaseDatabase: Firebase,
    private val coreRepository: CoreRepository
) : PunchRepository {

    /**
     * Resolves the user whose subcollection we read/write. Admin flows pass an explicit
     * [userId]; everything else falls back to the signed-in user. The `as String` cast
     * throws when the current user can't be resolved — callers run inside [errorWrapper]
     * (or the flow methods short-circuit before calling this), so it surfaces as an error.
     */
    private suspend fun resolveUserId(userId: String?): String =
        userId ?: (coreRepository.getUserId().data as String)

    override suspend fun getRates(): TSResult {
        return errorWrapper{
            val organizationIdResult = coreRepository.getOrganizationId()
            if (organizationIdResult.error != null) organizationIdResult
            val organizationId = organizationIdResult.data as String
            val ratesList = firebaseDatabase.firestore
                .collection("organizations")
                .document(organizationId)
                .collection("rates")
                .get()
                .documents
                .map { document -> document.data<RateDto>().copy(id = document.id).toRate() }

            TSResult(data = ratesList)
        }
    }

    override suspend fun updatePunch(punch: Punch, userId: String?): TSResult {
        return errorWrapper {
            val organizationIdResult = coreRepository.getOrganizationId()
            if (organizationIdResult.error != null) organizationIdResult
            val organizationId = organizationIdResult.data as String

            val userId = resolveUserId(userId)

            firebaseDatabase.firestore
                .collection("organizations")
                .document(organizationId)
                .collection("users")
                .document(userId)
                .collection("punches")
                .document(punch.punchId)
                .set(punch.toPunchDto(), merge = true)

            TSResult(data = "Punch updated successfully")
        }
    }

    override suspend fun updatePunchesWithNewRate(punchIn: Punch, punchOut: Punch?, userId: String?): TSResult {
        return errorWrapper {
            val updatePunchResult = updatePunch(punchIn, userId)
            if (punchOut != null) {
                updatePunch(punchOut, userId)
            }
            updatePunchResult
        }
    }

    @OptIn(ExperimentalTime::class)
    override suspend fun getPunches(startDate: LocalDateTime, endDate: LocalDateTime, userId: String?): Flow<TSResult> {
        val organizationIdResult = coreRepository.getOrganizationId()
        if (organizationIdResult.error != null) return flow { emit(organizationIdResult) }
        val organizationId = organizationIdResult.data as String

        val userId = if (userId != null) userId else {
            val userIdResult = coreRepository.getUserId()
            if (userIdResult.error != null) return flow { emit(userIdResult) }
            userIdResult.data as String
        }

        val startTimeStamp = startDate.toInstant(TimeZone.currentSystemDefault())
        val endTimeStamp = endDate.toInstant(TimeZone.currentSystemDefault())

        val flow = firebaseDatabase.firestore
            .collection("organizations")
            .document(organizationId)
            .collection("users")
            .document(userId)
            .collection("punches")
            .where {
                // all(...) AND-combines the constraints. Bare statements in a where{} block only
                // keep the LAST one, so both range bounds must be passed to all().
                all(
                    "dateTime" greaterThanOrEqualTo startTimeStamp.toString(),
                    "dateTime" lessThanOrEqualTo endTimeStamp.toString(),
                )
            }
            .snapshots
            .map { snapshot ->
                snapshot.documents.map { documentSnapshot ->
                documentSnapshot.data<PunchDto>().toPunch(punchId = documentSnapshot.id)
            }
            }.map { TSResult(data = it) }

        return FirestoreListenerRegistry.registerFlow(flow)
    }

    @OptIn(ExperimentalTime::class)
    override suspend fun getPunchesOnce(
        startDate: LocalDateTime,
        endDate: LocalDateTime,
        userId: String?,
        organizationId: String?,
    ): TSResult {
        return errorWrapper {
            val organizationId = organizationId ?: run {
                val organizationIdResult = coreRepository.getOrganizationId()
                if (organizationIdResult.error != null) return@errorWrapper organizationIdResult
                organizationIdResult.data as String
            }

            val resolvedUserId = resolveUserId(userId)

            val startTimeStamp = startDate.toInstant(TimeZone.currentSystemDefault())
            val endTimeStamp = endDate.toInstant(TimeZone.currentSystemDefault())

            val punches = firebaseDatabase.firestore
                .collection("organizations")
                .document(organizationId)
                .collection("users")
                .document(resolvedUserId)
                .collection("punches")
                .where {
                    all(
                        "dateTime" greaterThanOrEqualTo startTimeStamp.toString(),
                        "dateTime" lessThanOrEqualTo endTimeStamp.toString(),
                    )
                }
                .get()
                .documents
                .map { document -> document.data<PunchDto>().toPunch(punchId = document.id) }

            TSResult(data = punches)
        }
    }

    override suspend fun addPunch(punch: Punch, userId: String?): TSResult {
        return errorWrapper {
            val organizationIdResult = coreRepository.getOrganizationId()
            if (organizationIdResult.error != null) organizationIdResult
            val organizationId = organizationIdResult.data as String

            val userId = resolveUserId(userId)

            val result = firebaseDatabase.firestore
                .collection("organizations")
                .document(organizationId)
                .collection("users")
                .document(userId)
                .collection("punches")
                .add(punch.toPunchDto())

            TSResult(data = result.id)
        }
    }

    override suspend fun addHours(
        startPunch: Punch,
        endPunch: Punch,
        userId: String?
    ): TSResult {
        val addPunchResult = addPunch(startPunch, userId)
        val addPunch2Result = addPunch(endPunch, userId)
        return addPunchResult
    }

    override suspend fun deletePunches(punchIds: List<String?>, userId: String?): TSResult {
        return errorWrapper {
            val organizationIdResult = coreRepository.getOrganizationId()
            if (organizationIdResult.error != null) organizationIdResult
            val organizationId = organizationIdResult.data as String

            val userId = resolveUserId(userId)

            val resultList = mutableListOf<TSResult>()

            for (punchId in punchIds) {
                if (punchId != null) {
                    try {
                        firebaseDatabase.firestore
                            .collection("organizations")
                            .document(organizationId)
                            .collection("users")
                            .document(userId)
                            .collection("punches")
                            .document(punchId)
                            .delete()

                        resultList.add(TSResult(data = "Punch $punchId deleted successfully"))
                    } catch (e: Exception) {
                        resultList.add(TSResult(error = "Error deleting punch $punchId: ${e.message}"))
                    }
                }
            }

            // Return the first error if there is one, otherwise return the first success result
            resultList.firstOrNull { it.error != null } ?: resultList.firstOrNull() ?: TSResult(
                error = "No clock times processed"
            )
        }
    }

    override suspend fun updateTimeSheet(timeSheet: TimeSheet, userId: String?): TSResult {
        return errorWrapper {
            val organizationIdResult = coreRepository.getOrganizationId()
            if (organizationIdResult.error != null) organizationIdResult
            val organizationId = organizationIdResult.data as String

            val userId = resolveUserId(userId)

            firebaseDatabase.firestore
                .collection("organizations")
                .document(organizationId)
                .collection("users")
                .document(userId)
                .collection("timeSheets")
                .document(timeSheet.id)
                .set(timeSheet.toTimeSheetDto(), merge = true)

            TSResult(data = "Timesheet updated successfully")
        }
    }

    override suspend fun getTimeSheetById(id: String, userId: String?): Flow<TSResult> {
        val organizationIdResult = coreRepository.getOrganizationId()
        if (organizationIdResult.error != null) return flow { emit(organizationIdResult) }
        val organizationId = organizationIdResult.data as String

        val userId = if (userId != null) userId else {
            val userIdResult = coreRepository.getUserId()
            if (userIdResult.error != null) return flow { emit(userIdResult) }
            userIdResult.data as String
        }

        val flow = firebaseDatabase.firestore
            .collection("organizations")
            .document(organizationId)
            .collection("users")
            .document(userId)
            .collection("timeSheets")
            .document(id)
            .snapshots
            .map { snapshot -> snapshot.data<TimeSheetDto>().toTimeSheet(id = snapshot.id) }
            .map { TSResult(data = it) }

        return FirestoreListenerRegistry.registerFlow(flow)
    }

    // This function always adds, so if you want to subtract you need to pass a negative number
    override suspend fun adjustVacationTimeByHours(hours: Int, userId: String?): TSResult {
        return errorWrapper {
            val organizationIdResult = coreRepository.getOrganizationId()
            if (organizationIdResult.error != null) organizationIdResult
            val organizationId = organizationIdResult.data as String

            val userId = resolveUserId(userId)

            val userRef = firebaseDatabase.firestore
                .collection("organizations")
                .document(organizationId)
                .collection("users")
                .document(userId)

            val userObject = userRef.get().data<DatabaseUserDto>()
            val newPtoBalance = userObject.ptoBalance + hours

            if(newPtoBalance < 0) {
                TSResult(error = "You don't have enough PTO to do that")
            }else {
                // Update only the ptoBalance field
                userRef.update(mapOf("ptoBalance" to newPtoBalance))

                TSResult(data = "PTO balance updated successfully")
            }
        }
    }

    override suspend fun markTimeSheetsSubmitted(entries: List<Pair<String, String>>): TSResult {
        return errorWrapper {
            if (entries.isEmpty()) return@errorWrapper TSResult(data = "Nothing to submit")

            // A WriteBatch applies all writes atomically, but Firestore caps it at 500 ops.
            if (entries.size > MAX_BATCH_OPERATIONS) {
                return@errorWrapper TSResult(
                    error = "This export would update ${entries.size} timesheets, over the " +
                        "$MAX_BATCH_OPERATIONS-record limit for an atomic write."
                )
            }

            val organizationIdResult = coreRepository.getOrganizationId()
            if (organizationIdResult.error != null) return@errorWrapper organizationIdResult
            val organizationId = organizationIdResult.data as String

            val firestore = firebaseDatabase.firestore
            val usersCollection = firestore
                .collection("organizations")
                .document(organizationId)
                .collection("users")

            val batch = firestore.batch()
            entries.forEach { (userId, timeSheetId) ->
                val ref = usersCollection
                    .document(userId)
                    .collection("timeSheets")
                    .document(timeSheetId)
                batch.update(ref, "submitted" to true)
            }
            batch.commit()

            TSResult(data = "Pay period submitted")
        }
    }

    private companion object {
        const val MAX_BATCH_OPERATIONS = 500
    }
}